import { Button } from '@quadminds/mindkit';
import { AnimatePresence, motion } from 'framer-motion';
import Lottie from 'lottie-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AIAnimationData from '../../assets/json/lottie/onboarding/ai.json';
import KnowledgeAnimationData from '../../assets/json/lottie/onboarding/knowledge.json';
import TruckAnimationData from '../../assets/json/lottie/onboarding/truck.json';
import { useIsKnowledge } from '../../hooks/useIsKnowledge';

interface FeatureSliderProps {
    onNext: () => void;
}

const AUTO_ADVANCE_MS = 4500;

const slides: Array<{
    id: number;
    titleKey: string;
    descriptionKey: string;
    animationData: unknown;
    styles?: React.CSSProperties; // Optional styles for Lottie animation
}> = [
    {
        id: 1,
        titleKey: 'onboarding.features.slide1.title',
        descriptionKey: 'onboarding.features.slide1.description',
        animationData: TruckAnimationData,
    },
    {
        id: 2,
        titleKey: 'onboarding.features.slide2.title',
        descriptionKey: 'onboarding.features.slide2.description',
        animationData: KnowledgeAnimationData,
    },
    {
        id: 3,
        titleKey: 'onboarding.features.slide3.title',
        descriptionKey: 'onboarding.features.slide3.description',
        animationData: AIAnimationData,
    },
];

export const FeatureSlider = ({ onNext }: FeatureSliderProps) => {
    const { t } = useTranslation();
    const { isKnowledge } = useIsKnowledge();
    const visibleSlides = isKnowledge ? slides.filter((s) => s.id !== 1) : slides;
    const [currentSlide, setCurrentSlide] = useState(0);

    const handleNextSlide = () => {
        if (currentSlide < visibleSlides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            onNext();
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Auto-scroll logic: advance slide every 2 seconds
    const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Clear any previous timer
        if (autoScrollRef.current) {
            clearTimeout(autoScrollRef.current);
        }
        // Only auto-advance if not on last slide
        if (currentSlide < visibleSlides.length - 1) {
            autoScrollRef.current = setTimeout(() => {
                setCurrentSlide((prev) => prev + 1);
            }, AUTO_ADVANCE_MS);
        }
        // Cleanup on unmount or slide change
        return () => {
            if (autoScrollRef.current) {
                clearTimeout(autoScrollRef.current);
            }
        };
    }, [currentSlide, visibleSlides.length]);

    const slide = visibleSlides[currentSlide];

    return (
        <div className="p-8 flex-grow text-center">
            <div className="text-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={slide.id}
                        initial={{ opacity: 0, x: 24, scale: 0.98 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -24, scale: 0.98 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                        className="flex flex-col space-y-4 items-center"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.08, type: 'spring', stiffness: 300, damping: 22 }}
                        >
                            <Lottie
                                style={slide.styles ?? { width: 220, height: 220 }}
                                animationData={slide.animationData}
                                loop={true}
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.16, duration: 0.25 }}
                            className="flex flex-col items-center"
                        >
                            <h2 className="text-xl font-poppins font-semibold text-foreground mb-2">
                                {t(slide.titleKey)}
                            </h2>
                            <p className="text-muted-foreground max-w-[70%]">{t(slide.descriptionKey)}</p>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="flex items-center justify-between mt-8">
                <Button variant="ghost" onClick={handlePrevSlide} disabled={currentSlide === 0}>
                    <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex space-x-2">
                    {visibleSlides.map((_, index) => (
                        <div
                            key={index}
                            className={`h-1.5 rounded-full overflow-hidden bg-gray-300 dark:bg-gray-700 transition-all duration-300 ${
                                index === currentSlide ? 'w-6' : 'w-1.5'
                            }`}
                        >
                            {index === currentSlide && (
                                <motion.div
                                    key={currentSlide}
                                    className="h-full bg-secondary rounded-full"
                                    initial={{ width: '0%' }}
                                    animate={{ width: '100%' }}
                                    transition={{
                                        duration:
                                            currentSlide === visibleSlides.length - 1 ? 0.3 : AUTO_ADVANCE_MS / 1000,
                                        ease: 'linear',
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
                <Button variant="ghost" onClick={handleNextSlide}>
                    {currentSlide === visibleSlides.length - 1 ? (
                        t('onboarding.features.setup')
                    ) : (
                        <ChevronRight className="h-5 w-5" />
                    )}
                </Button>
            </div>
        </div>
    );
};
